<template>
<standard-dialog v-model="visible" scrollable persistent>

  <template slot="title">
    <span class="title">{{ $t('scene.edit')}}</span>
  </template>

  <v-form ref="form" v-model="validEditor" lazy-validation>

    <name-text-field v-model="nameText" :rules="rulesName"></name-text-field>

    <device-selector box multiple v-model="devices" :rules="rulesDevice" :label="$t('scene.select_device')" :gateways="[data.mac]" :ctrltypes="ctrltypes" clearable></device-selector>

    <template v-for="d in devices">
      <device-item flat :item="getDevice(d)" :eventlog="{}">
        <template slot-scope="data">
          <v-list-tile-action style="max-width:48px" class="ml-2">
            <state-selector v-model="values[data.device.key]" :item="data.device"></state-selector>
          </v-list-tile-action>
        </template>
      </device-item>
    </template>

  </v-form>

  <template slot="actions">
    <v-btn flat color="secondary" @click="$emit('enable',data)">{{ $t('scene.activate') }}</v-btn>
    <v-spacer></v-spacer>
    <v-btn :disabled="saving" :loading="removing" flat color="accent" @click="remove">{{ $t('scene.delete') }}</v-btn>
    <v-btn :disabled="!canSave || removing" :loading="saving" flat color="primary" @click="save">{{ $t('scene.save') }}</v-btn>
  </template>

</standard-dialog>
</template>

<script>
import UserAuth from '@/components/auth/UserAuth'
import Dialog from '@/components/utils/Dialog'
import NameTextField from '@/components/utils/NameTextField'
import DeviceItem from '@/components/device/DeviceItem'
import DeviceSelector from '@/components/device/DeviceSelector'
import DeviceStateSelector from '@/components/device/DeviceStateSelector'

export default {
  name: 'SceneEditorDialog',
  mixins: [UserAuth],
  props: {
    value: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object
    }
  },
  components: {
    'standard-dialog': Dialog,
    'name-text-field': NameTextField,
    'device-item': DeviceItem,
    'device-selector': DeviceSelector,
    'state-selector': DeviceStateSelector
  },
  data() {
    return {
      saving: false,
      removing: false,
      validEditor: false,
      nameText: null,
      devices: [],
      values: {},
      rulesName: [
        v => (v && (this.data.title == v || !this.sceneNames.includes(v))) || this.$t('scene.name_should_not_be_duplicated')
      ],
      rulesDevice: [
        v => !!v || this.$t('scene.device_is_required')
      ],
      ctrltypes: [23, 24, 25, 26, 31, 32, 33, 34, 35, 41]
    }
  },
  watch: {
    sceneNames(val) {
      if (this.saving) {
        if (val.includes(this.nameText)) {
          this.visible = false;
        }
      }
      if (this.removing) {
        if (!val.includes(this.data.title)) {
          this.visible = false;
        }
      }
    }
  },
  computed: {
    visible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    sceneNames() {
      return (this.userScene[this.data.mac] || []).map(s => s.title);
    },
    canSave() {
      return this.validEditor;
    }
  },
  methods: {
    getDevice(key) {
      let [mac, uid, cid] = key.split('-');
      let d = (((this.userDevice[mac] || {})[`${mac}-${uid}`] || {}).channel || {})[key] || {};
      return {
        ...d,
        key: key,
        targetName: ((this.userTargetName[mac] || {})[uid] || {})[cid]
      }
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      await this.removeScene();
      await this.saveScene();
      await this.refreshMac(this.data.mac);
    },
    async remove() {
      this.removing = true;
      await this.removeScene();
      await this.refreshMac(this.data.mac);
    },
    async saveScene() {
      let v = {
        scenes: [{
          title: this.nameText,
          comps: this.devices.map(d => {
            let [, uid, cid] = d.split('-');
            return {
              target: {
                dev: parseInt(uid),
                ch: parseInt(cid),
                if: (parseInt(uid) >>> 8) << 8
              },
              value: parseInt(this.values[d] || '0')
            }
          })
        }]
      };
      //console.log('SET', JSON.stringify(v))
      let ref = this.$db.ref('command/gateway').push();
      try {
        await ref.set({
          cmd: 'jsonsetall',
          mac: this.data.mac,
          val: v
        });
      } catch (err) {
        this.$emit('toast', this.$t('scene.permission_denied'));
        this.saving = false;
        return;
      }

      let refErr = ref.child('error');
      refErr.on('value', snapshot => {
        let val = snapshot.val();
        if (val) {
          ref.remove();
          this.$emit('toast', val);
          this.saving = false;
        }
      });

      setTimeout(() => {
        refErr.off();
      }, 5000);
    },
    async removeScene() {
      let ref = this.$db.ref('command/gateway').push();
      try {
        await ref.set({
          cmd: 'jsondel',
          mac: this.data.mac,
          val: {
            scenes: [{
              title: this.data.title
            }]
          }
        });
      } catch (err) {
        this.$emit('toast', this.$t('scene.permission_denied'));
        this.removing = false;
        return;
      }

      let refErr = ref.child('error');
      refErr.on('value', snapshot => {
        let val = snapshot.val();
        if (val) {
          ref.remove();
          this.$emit('toast', val);
          this.removing = false;
        }
      });

      setTimeout(() => {
        refErr.off();
      }, 5000);
    }
  },
  mounted() {
    this.$refs.form.reset();
    this.nameText = this.data.title;
    (this.data.comps || []).forEach(c => {
      let key = `${this.data.mac}-${c.target.dev}-${c.target.ch}`;
      this.devices.push(key);
      this.values[key] = c.value;
    });
  }
}
</script>



// WEBPACK FOOTER //
// src/components/scene/SceneEditorDialog.vue